import { useEffect, useRef, useState } from 'react';
import type { PopulatedMenu } from 'root/apiTypes';
import type { NavigateToFn } from 'root/clientTypes';
import type { DropdownOptionProps } from 'wix-ui-tpa/cssVars';
import { useExperiments, type ILocation, useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import stylesParams from 'root/components/Menus/stylesParams';
import { useGetDisplayStylesParams } from 'root/hooks/useGetDisplayStylesParams';
import { useSharedNavigationLogic } from 'root/components/Menus/NavigationWithSections/Navigation.shared.logic';
import { useSectionRefs } from 'root/contexts/SectionRefsContext';
import { findSectionIndex, getNavigationOffset, getScrollTop, getSectionIdFromSection } from './utils';
import { MenuListNavigation, MenuNavigationObject, SECTION_ID_URL, SECTION_ID_URL_PREFIX } from 'root/consts';
import { useNavigationBi } from 'root/components/Menus/Navigation/Navigation.bi';

type UseNavigationWithSectionsLogicParams = {
  menus: PopulatedMenu[];
  setActiveItem: (index: number) => void;
  navigateTo: NavigateToFn;
  currentUrl: ILocation['url'];
  activeMenuIndex: number;
};

export const useNavigationWithSectionsLogic = ({
  menus,
  setActiveItem,
  navigateTo,
  currentUrl = '',
  activeMenuIndex,
}: UseNavigationWithSectionsLogicParams) => {
  const { selectMenu } = useSharedNavigationLogic({
    menus,
    setActiveItem,
    navigateTo,
    currentUrl,
  });
  const { getSectionRef, sectionRefs } = useSectionRefs();
  const [activeSection, setActiveSection] = useState<number | undefined>(undefined);
  const isProgrammaticScroll = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastProgremmaticScrollPosition = useRef<number | undefined>(undefined);
  const [navigationOffset, setNavigationOffset] = useState<number>(0);
  const navigationContainerRef = useRef<HTMLDivElement>(null);
  const { reportMenuNavigationClick } = useNavigationBi();
  const { showTabs } = useGetDisplayStylesParams();
  const styles = useStyles();
  const navigationDropdownStyle = styles.get(stylesParams.navigationDropdownStyle);

  const { t } = useTranslation();

  const { experiments } = useExperiments();
  const isDropdownBottomExperimentEnabled = experiments.enabled('specs.restaurants.menusOoiDropdownBottom');

  const shouldShowMenusDropdown = menus.length > 1 && showTabs;

  const onClickNavigationDropdownOption = (option: DropdownOptionProps) => {
    selectMenu(Number(option.id));
  };

  const dropdownOptions = menus.map((menu, index): DropdownOptionProps => {
    return {
      id: index.toString(),
      value: menu?.name,
      isSelectable: true,
    };
  });

  const programmaticScrollTo = (targetTop: number) => {
    isProgrammaticScroll.current = true;
    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
    setTimeout(() => {
      lastProgremmaticScrollPosition.current = window.scrollY;
      isProgrammaticScroll.current = false;
    }, 1000);
  };

  const handleTabClick = (index: number) => {
    if (index !== undefined) {
      const sectionId = menus[activeMenuIndex].sections[index].id;
      if (sectionId) {
        const targetElementRef = getSectionRef(menus[activeMenuIndex].sections[index].id ?? '');
        const clampedScrollTop = getScrollTop(targetElementRef, navigationOffset);
        if (clampedScrollTop) {
          setActiveSection(index);
          window.history.replaceState(null, '', SECTION_ID_URL(sectionId));
          programmaticScrollTo(clampedScrollTop);
        }
      }
      reportMenuNavigationClick({
        navigationLayout: MenuListNavigation.Tabs,
        object: MenuNavigationObject.Sections,
        numOfSections: menus[activeMenuIndex].sections.length,
      });
    }
  };

  const createObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!window.IntersectionObserver) {
      return;
    }

    observerRef.current = new window.IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current || window.scrollY === lastProgremmaticScrollPosition.current) {
          return;
        }
        lastProgremmaticScrollPosition.current = undefined;

        const firstIntersectingEntry = entries.find((entry) => entry.isIntersecting);
        if (firstIntersectingEntry) {
          const sectionId = getSectionIdFromSection(firstIntersectingEntry.target.id);
          const sectionIndex = findSectionIndex(menus[activeMenuIndex].sections, sectionId);
          setActiveSection(sectionIndex);
          window.history.replaceState(null, '', SECTION_ID_URL(sectionId));
        }
      },
      { threshold: 0.6 }
    );

    sectionRefs.forEach((ref) => {
      if (ref?.current && observerRef.current) {
        observerRef.current.observe(ref.current);
      }
    });
  };

  useEffect(() => {
    setNavigationOffset(getNavigationOffset(navigationContainerRef.current));
    if (!sectionRefs || sectionRefs.length !== menus[activeMenuIndex].sections.length) {
      return;
    }

    const initialSectionTabId = currentUrl?.split(SECTION_ID_URL_PREFIX)?.[1];
    const initialSectionIndex =
      menus.length > 0 && activeMenuIndex >= 0 && activeMenuIndex < menus.length
        ? menus[activeMenuIndex]?.sections?.findIndex((section) => section.id === initialSectionTabId)
        : -1;
    const initialSectionTabIndex = initialSectionIndex !== -1 ? initialSectionIndex : undefined;

    if (initialSectionTabIndex !== activeSection && initialSectionTabIndex !== undefined) {
      const targetElementRef = getSectionRef(initialSectionTabId ?? '');

      const clampedScrollTop = getScrollTop(targetElementRef, navigationOffset);
      if (clampedScrollTop) {
        setActiveSection(initialSectionTabIndex);
        programmaticScrollTo(clampedScrollTop);
      }
    }
    createObserver();

    // Cleanup observer on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [sectionRefs]);

  return {
    dropdownOptions,
    onClickNavigationDropdownOption,
    navigationDropdownStyle,
    dropdowmPlacement: isDropdownBottomExperimentEnabled ? 'bottom' : undefined,
    dropdownFlip: isDropdownBottomExperimentEnabled ? false : undefined,
    t,
    shouldShowMenusDropdown,
    activeSection,
    showSectionNavigation: menus[activeMenuIndex]?.sections?.length > 0,
    navigationContainerRef,
    handleTabClick,
  };
};
